/**
 * From the outputs of one table script to the table the researcher sees:
 * the base first, every row of the layout present (a row the engine dropped
 * for a zero count is put back with zeros), the statistics of the mean
 * outputs where the layout places them, differences such as NPS computed
 * here (a grid summary's NPS of each row too, and the Total and the shares
 * of a sum summary), the banner columns renamed, and what the check of a
 * table from the data found. A grid of the data whose rows are sibling
 * containers is one table made of the table of each sibling. Last, the rows
 * are sorted and the empty ones left out as the plan asks (`arrangeRows`):
 * a table that hides them puts back no answer row the engine dropped.
 * Everything here is pure.
 */

import type {
  AxisLeaf,
  CellSig,
  CellSigLetter,
  ModelCell,
  SigElement,
  SigMeta,
  TableModel,
} from "utils/tableModel"
import {
  buildHeaderTree,
  buildTableModel,
  resolveStatFormat,
  SIG_STAT,
} from "utils/tableModel"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { EngineOutputItem, RTableContent } from "types/engineOutput.types"
import { DownRow, downRowsOf, withDownRows } from "features/Dashboard/runtime/downRows"
import { BannerColumn, BannerPlan } from "../compiler/banner"
import { TOTAL_LABEL } from "../compiler/names"
import {
  isScoreSlot,
  leftOutRows,
  OutputRole,
  RowSlot,
  SiblingPart,
  SiblingPlan,
  siblingSentences,
  slotNamed,
  TablePlan,
  WEIGHTED_BASE,
} from "../compiler/table"
import { arrangeRows } from "./arrange"
import { readStatGrid, StatGrid, statValue } from "./statGrid"

export class ResultError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "ResultError"
  }
}

export interface TableResult {
  model: TableModel
  notes: string[]
  /** What the run found that the table must say: the check's outcome. */
  warnings: string[]
}

/**
 * Column key of the Totals column; the mean-total output holds it as "Total".
 * Not the `TOTALS_KEY` of `constants/tableConversion` ("Totals"), which is the
 * engine's own label of the Totals leaf.
 */
export const TOTALS_KEY = "__totals__"
export const columnKey = (leaf: AxisLeaf): string =>
  leaf.isTotal ? TOTALS_KEY : leaf.path.join("//")

const SEPARATOR = "//"
const BASE_LABEL = "Base"
/**
 * Statistics of the values: there is none in a column where nobody has a
 * value (a grid row's share and sum included). The engine leaves the
 * standard deviation and the standard error blank below 2 respondents
 * itself, and the median where nobody has a positive weight.
 */
const VALUE_STATS: string[] = [
  STATISTIC_TYPE.AVG,
  STATISTIC_TYPE.MIN,
  STATISTIC_TYPE.MAX,
  STATISTIC_TYPE.STDDEV,
  STATISTIC_TYPE.STDERR,
  STATISTIC_TYPE.MEDIAN,
  STATISTIC_TYPE.PERC,
  STATISTIC_TYPE.SUM,
]
/** The statistics the engine tests in a mean output: the mean of a number, the share of a yes or no field. */
const TESTED_STATS: string[] = [STATISTIC_TYPE.AVG, STATISTIC_TYPE.PERC]
/** The rows of the Totals block that make the base. */
const COUNT_STATS: string[] = [STATISTIC_TYPE.N, STATISTIC_TYPE.BIG_N]
/** One letter of a `sig` cell: "A", "b", or "A1" past the end of the alphabet. */
const LETTER = /[A-Za-z]\d*/g
const POINTS_FORMAT = { decimals: 1, thousands: false, percent: false }
/** What the line under a sum summary's row says: its share of the Total. */
const SHARE_LABEL = "% of total"

/** The outputs of the script by role. */
interface Contents {
  main: RTableContent
  check?: RTableContent
  mean?: RTableContent
  meanTotal?: RTableContent
  meanExcl0?: RTableContent
  meanExcl0Total?: RTableContent
}

/** One set of mean parts: by the banner, over the total table, and as a model. */
interface MeanParts {
  mean: StatGrid | null
  meanTotal: StatGrid | null
  /** The mean output as a model, for the significance of its cells; only on a tested table. */
  meanModel: TableModel | null
}

/** What the slots are built from. */
interface Parts extends MeanParts {
  plan: TablePlan
  main: TableModel
  /** `columnKey` of every column of the main output, in order. */
  keys: string[]
  /** The mean parts without the zeros, which the mean excluding 0 reads. */
  excl0: MeanParts
  /** The significance of the finished table. */
  significance: SigMeta | undefined
}

/** A run of rows of the main output that share one label: a row with its statistics. */
interface Block {
  label: string
  isTotal: boolean
  rows: DownRow[]
}

const numeric = (cell: ModelCell | undefined): number | null =>
  cell && typeof cell.raw === "number" && Number.isFinite(cell.raw) ? cell.raw : null

// ---------------------------------------------------------------------------
// The outputs
// ---------------------------------------------------------------------------

const isTable = (item: EngineOutputItem | undefined): item is EngineOutputItem =>
  item !== undefined &&
  (item.type === "rtable" ||
    Array.isArray((item.content as { data?: unknown } | undefined)?.data))

/**
 * The content of each output by its role. `outputs[i]` is `plan.outputs[i]`:
 * matched by position, never by label (with a universe the engine labels an
 * output `<name>-<universe>`).
 */
const readOutputs = (plan: TablePlan, outputs: EngineOutputItem[]): Contents => {
  const byRole: Partial<Record<OutputRole, RTableContent>> = {}
  plan.outputs.forEach((role, i) => {
    const item = outputs[i]
    if (!isTable(item)) throw new ResultError("The engine returned no table.")
    byRole[role] = item.content as RTableContent
  })
  if (!byRole.main) throw new ResultError("The engine returned no table.")
  return {
    main: byRole.main,
    check: byRole.check,
    mean: byRole.mean,
    meanTotal: byRole["mean-total"],
    meanExcl0: byRole["mean-excl-0"],
    meanExcl0Total: byRole["mean-excl-0-total"],
  }
}

// ---------------------------------------------------------------------------
// The check of a table from the data
// ---------------------------------------------------------------------------

const CHECK_ROWS = ["None", "Several"] as const

/**
 * The counts of the check's output (facts.md, fact 12), read by label: the
 * row whose first cell is "None" (or "Several") and whose second is "n"
 * holds the count in its third. A row that is not there is 0: the engine
 * drops zero rows, and the Totals row too when one row is left.
 */
export const readCheck = (content: RTableContent): { none: number; several: number } => {
  if (!Array.isArray((content as { data?: unknown } | undefined)?.data))
    throw new ResultError("The engine returned no check for this table.")
  const counts = { None: 0, Several: 0 }
  content.data.forEach((row) => {
    const [label, stat, value] = row
    if (
      CHECK_ROWS.includes(label as (typeof CHECK_ROWS)[number]) &&
      stat === "n" &&
      typeof value === "number" &&
      Number.isFinite(value)
    )
      counts[label as (typeof CHECK_ROWS)[number]] = value
  })
  return { none: counts.None, several: counts.Several }
}

const respondents = (count: number): string =>
  count === 1 ? "1 respondent in the base has" : `${count} respondents in the base have`

/**
 * What the check found, as the middle of a sentence: "21 respondents in the
 * base have no category marked", "1 respondent in the base has more than one
 * category marked", "21 respondents in the base have no category marked and
 * 248 have more than one". Called with at least one count above 0.
 */
export const checkCounts = (none: number, several: number): string => {
  if (several === 0) return `${respondents(none)} no category marked`
  if (none === 0) return `${respondents(several)} more than one category marked`
  return `${respondents(none)} no category marked and ${several} ${
    several === 1 ? "has" : "have"
  } more than one`
}

/**
 * The layout the finished table is built from, and what the table must say,
 * after the check: with nobody without a category or with several, the plan's
 * own layout and nothing. Respondents with several categories cost the table
 * its mean and every difference row (scores need exactly one category per
 * respondent), on a scale as on single choice. Respondents with none are
 * outside the mean anyway, so they cost nothing and are only counted. A table
 * that loses nothing says its rows do not add to 100.
 */
const afterCheck = (
  plan: TablePlan,
  content: RTableContent | undefined
): { layout: RowSlot[]; warnings: string[] } => {
  if (!plan.check) return { layout: plan.layout, warnings: [] }
  if (!content) throw new ResultError("The engine returned no check for this table.")
  const { none, several } = readCheck(content)
  if (none + several === 0) return { layout: plan.layout, warnings: [] }
  const counts = checkCounts(none, several)
  const gone = several > 0 ? leftOutRows(plan.layout) : null
  if (!gone) {
    const shown = plan.check.kind === "scale" ? "a scale" : "single choice"
    return {
      layout: plan.layout,
      warnings: [`Shown as ${shown}, but ${counts}, so the rows do not add to 100.`],
    }
  }
  return {
    layout: plan.layout.filter((slot) => !isScoreSlot(slot)),
    warnings: [
      `${gone.what} ${gone.verb} left out because ${counts}. Scores need exactly one category per respondent.`,
    ],
  }
}

// ---------------------------------------------------------------------------
// The main model
// ---------------------------------------------------------------------------

/**
 * Whether the main output has rows with a statistic. For a statistic name it
 * does not know the engine returns the header and one "Totals" row without a
 * statistic or a value.
 */
const hasRows = (main: TableModel): boolean => {
  const statLeaves = main.statAxis === "down" ? main.down.leaves : main.across.leaves
  return main.down.leaves.length > 0 && statLeaves.some((leaf) => !!leaf.stat)
}

// ---------------------------------------------------------------------------
// The statistics of the mean outputs
// ---------------------------------------------------------------------------

/** A mean output read as a grid. One that holds no statistics is an error, not a blank row. */
const readMeanGrid = (content: RTableContent | undefined): StatGrid | null => {
  if (!content) return null
  const grid = readStatGrid(content)
  if (grid.columns.length === 0 || grid.rows.length === 0)
    throw new ResultError("The engine returned no statistics for this table.")
  return grid
}

/** The fields of the mean outputs a slot reads: a statistic's own, or both sides of a difference of fields. */
const fieldsOf = (slot: RowSlot): string[] =>
  slot.kind === "stat"
    ? slot.field === null
      ? []
      : [slot.field]
    : slot.kind === "difference" && slot.fields
      ? [slot.plus, slot.minus]
      : []

/** Every field a slot names must be a row of the mean outputs. */
const checkFields = (layout: RowSlot[], mean: StatGrid | null, meanTotal: StatGrid | null): void =>
  layout.forEach((slot) =>
    fieldsOf(slot).forEach((field) => {
      const has = (grid: StatGrid): boolean => grid.rows.some((row) => row.label === field)
      if (!mean || !has(mean) || (meanTotal && !has(meanTotal)))
        throw new ResultError(`The engine returned no statistics for "${field}".`)
    })
  )

/** The mean parts a statistic slot reads: those without the zeros for the mean excluding 0. */
const meanPartsOf = (parts: Parts, slot: Extract<RowSlot, { kind: "stat" }>): MeanParts =>
  slot.excludingZero ? parts.excl0 : parts

/** `stat` in the column `key` of the mean outputs `from`, for `field` (null = the one field). */
const meanStat = (
  from: MeanParts,
  field: string | null,
  key: string,
  stat: string
): string | number | null => {
  const grid = key === TOTALS_KEY ? from.meanTotal : from.mean
  if (!grid) return null
  const column = key === TOTALS_KEY ? TOTAL_LABEL : key
  if (VALUE_STATS.includes(stat)) {
    // An empty group comes back with base n 0 and avg 0: that 0 is not a mean.
    const base = statValue(grid, field, column, STATISTIC_TYPE.BASE_N)
    if (typeof base !== "number" || base === 0) return null
  }
  const raw = statValue(grid, field, column, stat)
  return raw === "" ? null : raw
}

/**
 * The significance the engine gave the `stat` cell (`avg` or `perc`) of
 * `field` in the column `key` of the mean output (tested, skipped, low
 * base, n).
 */
const meanCellSig = (
  meanModel: TableModel | null,
  field: string | null,
  key: string,
  stat: string
): CellSig | undefined => {
  if (!meanModel) return undefined
  const r =
    field === null
      ? 0
      : meanModel.down.leaves.findIndex((leaf) => leaf.path.join(SEPARATOR) === field)
  const c = meanModel.across.leaves.findIndex(
    (leaf) => columnKey(leaf) === key && leaf.stat === stat
  )
  return r >= 0 && c >= 0 ? meanModel.cells[r]?.[c]?.sig : undefined
}

// ---------------------------------------------------------------------------
// The letters of the mean
// ---------------------------------------------------------------------------

/**
 * The letters of a `sig` cell of the mean output `mean`, written with the
 * letters of the finished table: DTAB letter to banner path, banner path to
 * the table's element. A letter whose column is not in the table is dropped.
 * Lower case (the second confidence level) stays lower case.
 */
const translateLetters = (text: string, mean: StatGrid, parts: Parts): CellSigLetter[] => {
  const pathOf = new Map(
    Object.entries(mean.letters).map(([path, letter]) => [letter.toUpperCase(), path])
  )
  const elements = parts.significance?.elements ?? []
  return (text.match(LETTER) ?? []).flatMap((token): CellSigLetter[] => {
    const path = pathOf.get(token.toUpperCase())
    const element =
      path === undefined ? -1 : elements.findIndex((e) => e.header.join(SEPARATOR) === path)
    const letter = elements[element]?.letter
    if (!letter) return []
    const lower = token !== token.toUpperCase()
    return [
      {
        letter: lower ? letter.toLowerCase() : letter.toUpperCase(),
        level: lower ? "lower" : "upper",
        element,
      },
    ]
  })
}

/**
 * The significance of the finished table: the main output's. A numeric table
 * and a grid summary are tested in the mean output only (their main output
 * is a count without SIG): the mean output's significance is then taken
 * over, with one element per column of the main output, and the mean (or a
 * grid row's share) keeps its own letters.
 */
const tableSignificance = (
  plan: TablePlan,
  main: TableModel,
  meanModel: TableModel | null
): SigMeta | undefined => {
  if (main.significance || !plan.significance) return main.significance
  const meta = meanModel?.significance
  if (!meta) return undefined
  const counts = main.down.leaves.findIndex((leaf) => leaf.stat === STATISTIC_TYPE.N)
  const elements = main.across.leaves.map((leaf, c): SigElement => {
    const span: [number, number] = [c, c + 1]
    const own = leaf.isTotal
      ? undefined
      : meta.elements.find((e) => e.header.join(SEPARATOR) === columnKey(leaf))
    if (own) return { ...own, span }
    return {
      span,
      header: [...leaf.path],
      letter: null,
      group: null,
      baseN: numeric(main.cells[counts]?.[c]) ?? 0,
      lowBase: false,
      total: leaf.isTotal,
    }
  })
  // A column the skeleton dropped leaves its group's letters.
  const shown = new Set(elements.flatMap((e) => (e.letter ? [e.letter] : [])))
  const groups = meta.groups.map((group) => ({
    ...group,
    letters: (group.letters.match(LETTER) ?? []).filter((l) => shown.has(l)).join(""),
  }))
  return { ...meta, bannerAxis: "across", elements, groups }
}

// ---------------------------------------------------------------------------
// The blocks of the main model
// ---------------------------------------------------------------------------

const blocksOf = (main: TableModel): Block[] =>
  downRowsOf(main).reduce<Block[]>((blocks, row) => {
    const label = row.leaf.path.join(SEPARATOR)
    const last = blocks[blocks.length - 1]
    if (last && last.label === label && last.isTotal === row.leaf.isTotal) last.rows.push(row)
    else blocks.push({ label, isTotal: row.leaf.isTotal, rows: [row] })
    return blocks
  }, [])

// ---------------------------------------------------------------------------
// The rows, slot by slot
// ---------------------------------------------------------------------------

/** `row` shown as `display`, in block `element` of the finished table. */
const relabel = (row: DownRow, display: string, element: number): DownRow => ({
  ...row,
  leaf: {
    ...row.leaf,
    id: `${display}#${row.leaf.stat ?? ""}`,
    path: [display],
    elementIndex: element,
  },
})

/** The rows of a row the engine dropped for a zero count, shaped like `template`. */
const zeroRows = (template: Block): DownRow[] =>
  template.rows.map((row) => ({
    leaf: { id: "", path: [], stat: row.leaf.stat, isTotal: false },
    cells: row.cells.map(
      (cell): ModelCell => ({
        raw: cell.kind === "letters" ? "" : 0,
        stat: cell.stat,
        kind: cell.kind,
        format: cell.format,
        ...(cell.role ? { role: cell.role } : {}),
      })
    ),
  }))

/** `row` shown as the Base of the table: a row of the Totals block, whatever its label. */
const asBase = (row: DownRow, element: number): DownRow => {
  const base = relabel(row, BASE_LABEL, element)
  return { ...base, leaf: { ...base.leaf, isTotal: true } }
}

/**
 * The Base of a table whose main output is the total table (a grid
 * summary): its one row, everyone in the table's population.
 */
const totalBaseRows = (blocks: Block[], element: number, notes: string[]): DownRow[] => {
  const total = blocks.find((b) => !b.isTotal && b.label === TOTAL_LABEL)
  const counts = (total?.rows ?? []).filter((row) => COUNT_STATS.includes(row.leaf.stat ?? ""))
  if (counts.length === 0) notes.push("The engine returned no base for this table.")
  return counts.map((row) => asBase(row, element))
}

const baseRows = (blocks: Block[], element: number, notes: string[]): DownRow[] => {
  const totals = blocks.find((b) => b.isTotal)
  if (!totals) {
    notes.push("Only one row has answers, so the engine returned no base for this table.")
    return []
  }
  const counts = totals.rows.filter((row) => COUNT_STATS.includes(row.leaf.stat ?? ""))
  if (counts.length === 0) notes.push("The table has no count statistic, so it shows no base.")
  return counts.map((row) => relabel(row, BASE_LABEL, element))
}

/** The Totals counts of a number table's rows, and the statistics they stand in for. */
const STATS_BASES: [string, string][] = [
  [STATISTIC_TYPE.N, STATISTIC_TYPE.BASE_N],
  [STATISTIC_TYPE.BIG_N, WEIGHTED_BASE],
]

/**
 * The Base of a number table's rows (`stats`): the Totals block, or, when
 * the engine dropped it (one row left), the statistics' own base, the same
 * people (those with a value), as the Totals would show it.
 */
const statsBaseRows = (
  blocks: Block[],
  parts: Parts,
  element: number,
  notes: string[]
): DownRow[] => {
  if (blocks.some((b) => b.isTotal)) return baseRows(blocks, element, notes)
  const rows = STATS_BASES.flatMap(([stat, from]): DownRow[] => {
    const raws = parts.keys.map((key) => meanStat(parts, null, key, from))
    if (raws.every((raw) => raw === null)) return []
    const format = resolveStatFormat(stat, undefined)
    return [
      {
        leaf: { ...slotLeaf(BASE_LABEL, stat, element), isTotal: true },
        cells: raws.map((raw): ModelCell => ({ raw, stat, ...format })),
      },
    ]
  })
  if (rows.length === 0) return baseRows(blocks, element, notes)
  return rows
}

/**
 * Every row the engine returned must be one the layout asks for: a row under
 * another label would otherwise show as zeros while its figures are thrown
 * away. A layout without row slots (a numeric table, a grid summary) shows
 * none of the skeleton's rows.
 */
const checkRowsAsked = (layout: RowSlot[], blocks: Block[]): void => {
  const asked = new Set(layout.flatMap((slot) => (slot.kind === "row" ? [slot.label] : [])))
  if (asked.size === 0) return
  const stray = blocks.find((b) => !b.isTotal && !asked.has(b.label))
  if (stray)
    throw new ResultError(`The engine returned a row the table did not ask for: "${stray.label}".`)
}

/**
 * The rows of a row slot: the engine's, or zeros when it dropped the row.
 * A value of a number nobody gave is left out instead (spec D10), and so is
 * an answer nobody gave when the table hides them (`hideEmpty`); a net
 * nobody is in is always put back.
 */
const optionRows = (
  slot: Extract<RowSlot, { kind: "row" }>,
  blocks: Block[],
  element: number,
  hideEmpty: boolean
): DownRow[] => {
  const block = blocks.find((b) => !b.isTotal && b.label === slot.label)
  if (!block && (slot.value || (hideEmpty && !slot.net))) return []
  const template = blocks.find((b) => !b.isTotal) ?? blocks[0]
  const rows = block ? block.rows : zeroRows(template)
  return rows.map((row) => relabel(row, slot.display, element))
}

/** The leaf of statistic `stat` of a slot shown as `display`, in block `element`. */
const slotLeaf = (display: string, stat: string, element: number, statLabel?: string): AxisLeaf => ({
  id: `${display}#${stat}`,
  path: [display],
  stat,
  ...(statLabel ? { statLabel } : {}),
  isTotal: false,
  elementIndex: element,
})

/** A raw value as a number; null for a blank cell or text. */
const asNumber = (raw: string | number | null): number | null =>
  typeof raw === "number" && Number.isFinite(raw) ? raw : null

/**
 * A grid row's own base statistics (`bases`: "base n", and "base N" when
 * weighted) of `field` in the mean outputs, as rows above its value.
 */
const baseStatRows = (
  bases: string[] | undefined,
  field: string,
  parts: Parts,
  display: string,
  element: number
): DownRow[] =>
  (bases ?? []).map((stat) => {
    const format = resolveStatFormat(stat, undefined)
    return {
      leaf: slotLeaf(display, stat, element),
      cells: parts.keys.map(
        (key): ModelCell => ({ raw: meanStat(parts, field, key, stat), stat, ...format })
      ),
    }
  })

/**
 * The sum of a sum summary's rows in each column (the fields of its "row"
 * slots, a blank sum counted as 0); null in a column where every one is
 * blank.
 */
const sumTotals = (parts: Parts): (number | null)[] => {
  const fields = parts.plan.layout.flatMap((s) =>
    s.kind === "stat" && s.sum === "row" && s.field !== null ? [s.field] : []
  )
  return parts.keys.map((key) => {
    const values = fields.map((f) => asNumber(meanStat(parts, f, key, STATISTIC_TYPE.SUM)))
    return values.every((v) => v === null) ? null : values.reduce<number>((a, v) => a + (v ?? 0), 0)
  })
}

/**
 * A slot of a sum summary: the Total (the sum of every row's sum), or a
 * row: its own bases, its sum, and its share of the Total, in % (a fraction
 * shown as a percentage, as `perc` is).
 */
const sumRows = (
  slot: Extract<RowSlot, { kind: "stat" }>,
  parts: Parts,
  element: number
): DownRow[] => {
  const sumFormat = resolveStatFormat(STATISTIC_TYPE.SUM, undefined)
  const totals = sumTotals(parts)
  const sumRow = (values: (number | null)[]): DownRow => ({
    leaf: slotLeaf(slot.display, STATISTIC_TYPE.SUM, element),
    cells: values.map((raw): ModelCell => ({ raw, stat: STATISTIC_TYPE.SUM, ...sumFormat })),
  })
  if (slot.sum === "total" || slot.field === null) return [sumRow(totals)]
  const field = slot.field
  const sums = parts.keys.map((key) => asNumber(meanStat(parts, field, key, STATISTIC_TYPE.SUM)))
  const shareFormat = resolveStatFormat(STATISTIC_TYPE.PERC, undefined)
  const shares = sums.map((sum, c) => {
    const total = totals[c]
    return sum === null || total === null || total === 0 ? null : sum / total
  })
  return [
    ...baseStatRows(slot.bases, field, parts, slot.display, element),
    sumRow(sums),
    {
      leaf: slotLeaf(slot.display, STATISTIC_TYPE.PERC, element, SHARE_LABEL),
      cells: shares.map((raw): ModelCell => ({ raw, stat: STATISTIC_TYPE.PERC, ...shareFormat })),
    },
  ]
}

/**
 * One statistic of the mean outputs, under the row's own bases when it has
 * them. The mean (or a grid row's share) of a tested table gets its letters
 * as `buildTableModel` gives them to a tested row: the engine's
 * significance on the cell and a `sig` row under it (its letters position is
 * set once the rows are in place, see `withLettersAt`).
 */
const statRows = (
  slot: Extract<RowSlot, { kind: "stat" }>,
  parts: Parts,
  element: number
): DownRow[] => {
  if (slot.sum) return sumRows(slot, parts, element)
  const leafOf = (stat: string): AxisLeaf => slotLeaf(slot.display, stat, element)
  const format = resolveStatFormat(slot.stat, undefined)
  // The mean excluding 0 reads its own mean parts, letters included.
  const from = meanPartsOf(parts, slot)
  const cells = parts.keys.map(
    (key): ModelCell => ({
      raw: meanStat(from, slot.field, key, slot.stat),
      stat: slot.stat,
      ...format,
    })
  )
  const bases =
    slot.field === null ? [] : baseStatRows(slot.bases, slot.field, parts, slot.display, element)
  const { mean } = from
  const tested =
    parts.plan.significance &&
    TESTED_STATS.includes(slot.stat) &&
    mean !== null &&
    mean.columns.some((column) => column.stat === SIG_STAT)
  if (!tested) return [...bases, { leaf: leafOf(slot.stat), cells }]

  const letters = parts.keys.map((key) =>
    key === TOTALS_KEY
      ? []
      : translateLetters(String(statValue(mean, slot.field, key, SIG_STAT) ?? ""), mean, parts)
  )
  const testedCells = cells.map((cell, c): ModelCell => {
    const engine = meanCellSig(from.meanModel, slot.field, parts.keys[c], slot.stat)
    if (!engine) return cell
    const sig: CellSig = { ...engine, letters: letters[c] }
    delete sig.lettersAt
    return { ...cell, sig }
  })
  const lettersFormat = resolveStatFormat(SIG_STAT, undefined)
  const lettersCells = letters.map(
    (list): ModelCell => ({
      raw: list.map((l) => l.letter).join(""),
      stat: SIG_STAT,
      ...lettersFormat,
      role: "letters",
    })
  )
  return [
    ...bases,
    { leaf: leafOf(slot.stat), cells: testedCells },
    { leaf: leafOf(SIG_STAT), cells: lettersCells },
  ]
}

/** `plus` minus `minus`, two shares, in points: a row of the finished table, never tested. */
const pointsRow = (
  display: string,
  stat: string,
  plus: (number | null)[],
  minus: (number | null)[],
  element: number
): DownRow => ({
  leaf: slotLeaf(display, stat, element, "points"),
  cells: plus.map((p, c): ModelCell => {
    const m = minus[c]
    return {
      raw: p === null || m === null ? null : (p - m) * 100,
      stat,
      kind: "mean",
      format: POINTS_FORMAT,
    }
  }),
})

/**
 * A difference of two fields of the mean outputs (a grid row's NPS: its
 * promoters minus its detractors) on their `perc`, each column and the
 * Totals, under the row's own bases (those of `plus`, the same people).
 */
const fieldDifferenceRows = (
  slot: Extract<RowSlot, { kind: "difference" }>,
  parts: Parts,
  element: number
): DownRow[] => {
  const shares = (field: string): (number | null)[] =>
    parts.keys.map((key) => asNumber(meanStat(parts, field, key, STATISTIC_TYPE.PERC)))
  return [
    ...baseStatRows(slot.bases, slot.plus, parts, slot.display, element),
    pointsRow(slot.display, STATISTIC_TYPE.PERC, shares(slot.plus), shares(slot.minus), element),
  ]
}

/** `plus` minus `minus` on the column percentage, in points. */
const differenceRows = (
  slot: Extract<RowSlot, { kind: "difference" }>,
  parts: Parts,
  blocks: Block[],
  element: number,
  notes: string[]
): DownRow[] => {
  if (slot.fields) return fieldDifferenceRows(slot, parts, element)
  const pct = STATISTIC_TYPE.COL_PERCENT
  if (!parts.main.down.leaves.some((leaf) => leaf.stat === pct)) {
    notes.push(`${slot.display} needs the column percentage and was left out.`)
    return []
  }
  const percents = (label: string): (number | null)[] => {
    const row = blocks
      .find((b) => !b.isTotal && b.label === label)
      ?.rows.find((r) => r.leaf.stat === pct)
    // A net the engine dropped had nobody in it: 0 in every column.
    return parts.keys.map((_, c) => (row ? numeric(row.cells[c]) : 0))
  }
  return [pointsRow(slot.display, pct, percents(slot.plus), percents(slot.minus), element)]
}

/** A mean row and the row that prints its letters, as indices of the finished table. */
interface LettersLink {
  row: number
  letters: number
}

/** A slot of the layout and where its rows are in the finished table (`to` exclusive). */
interface SlotSpan {
  slot: RowSlot
  from: number
  to: number
}

/**
 * The field of the grid row a slot of a grid summary shows (an NPS row's
 * promoters, who are the same people); null for any other slot.
 */
const gridRowField = (slot: RowSlot): string | null =>
  slot.kind === "stat" && slot.field !== null && slot.sum !== "total"
    ? slot.field
    : slot.kind === "difference" && slot.fields
      ? slot.plus
      : null

/**
 * Whether nobody answered a grid row of a summary: its own base n in the
 * Totals (in the one column when the engine left only one) is 0.
 */
const nobodyAnswered = (field: string, parts: Parts): boolean => {
  const key = parts.keys.includes(TOTALS_KEY)
    ? TOTALS_KEY
    : parts.keys.length === 1
      ? parts.keys[0]
      : null
  return key !== null && asNumber(meanStat(parts, field, key, STATISTIC_TYPE.BASE_N)) === 0
}

const layoutRows = (
  parts: Parts,
  notes: string[]
): { rows: DownRow[]; links: LettersLink[]; spans: SlotSpan[] } => {
  const blocks = blocksOf(parts.main)
  checkRowsAsked(parts.plan.layout, blocks)
  const { hideEmpty } = parts.plan
  const rows: DownRow[] = []
  const links: LettersLink[] = []
  const spans: SlotSpan[] = []
  let element = 0
  parts.plan.layout.forEach((slot) => {
    // A table that hides empty rows leaves out a grid row nobody answered.
    const field = hideEmpty ? gridRowField(slot) : null
    if (field !== null && nobodyAnswered(field, parts)) return
    const built =
      slot.kind === "base"
        ? slot.total
          ? totalBaseRows(blocks, element, notes)
          : slot.stats
            ? statsBaseRows(blocks, parts, element, notes)
            : baseRows(blocks, element, notes)
        : slot.kind === "row"
          ? optionRows(slot, blocks, element, hideEmpty)
          : slot.kind === "stat"
            ? statRows(slot, parts, element)
            : differenceRows(slot, parts, blocks, element, notes)
    if (built.length === 0) return
    // The letters follow the tested row, below the row's own bases if it has them.
    const sigAt = slot.kind === "stat" ? built.findIndex((row) => row.leaf.stat === SIG_STAT) : -1
    if (sigAt > 0) links.push({ row: rows.length + sigAt - 1, letters: rows.length + sigAt })
    spans.push({ slot, from: rows.length, to: rows.length + built.length })
    rows.push(...built)
    element++
  })
  return { rows, links, spans }
}

/** `model` with each linked mean cell pointing at the cell that prints its letters. */
const withLettersAt = (model: TableModel, links: LettersLink[]): TableModel => {
  if (links.length === 0) return model
  const cells = [...model.cells]
  links.forEach(({ row, letters }) => {
    cells[row] = cells[row].map((cell, col) =>
      cell.sig ? { ...cell, sig: { ...cell.sig, lettersAt: { row: letters, col } } } : cell
    )
  })
  return { ...model, cells }
}

// ---------------------------------------------------------------------------
// The column names
// ---------------------------------------------------------------------------

/** The banner column an across leaf stands for: its path ends with the group and the label. */
const bannerColumnOf = (leaf: AxisLeaf, banner: BannerPlan): BannerColumn | undefined => {
  if (leaf.isTotal || leaf.path.length < 2) return undefined
  const [group, label] = leaf.path.slice(-2)
  return banner.columns.find((column) => column.group === group && column.label === label)
}

/**
 * The banner columns under the names the table shows: the last two segments
 * of a column's path (the group heading and the column label) become
 * `groupDisplay` and `display`.
 */
const renameColumns = (model: TableModel, banner: BannerPlan | null): TableModel => {
  if (!banner) return model
  const renamed = new Map<string, [string, string]>()
  const leaves = model.across.leaves.map((leaf) => {
    const column = bannerColumnOf(leaf, banner)
    if (!column || (column.display === column.label && column.groupDisplay === column.group))
      return leaf
    const shown: [string, string] = [column.groupDisplay, column.display]
    const path = [...leaf.path.slice(0, -2), ...shown]
    renamed.set(leaf.path.join(SEPARATOR), shown)
    return { ...leaf, id: path.join(SEPARATOR), path }
  })
  if (renamed.size === 0) return model
  const renamedModel: TableModel = {
    ...model,
    across: { ...model.across, leaves, tree: buildHeaderTree(leaves) },
  }
  if (model.significance) {
    renamedModel.significance = {
      ...model.significance,
      elements: model.significance.elements.map((e) => {
        const shown = renamed.get(e.header.join(SEPARATOR))
        return shown === undefined ? e : { ...e, header: [...e.header.slice(0, -2), ...shown] }
      }),
    }
  }
  return renamedModel
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

/** The banner columns the engine dropped because nobody in the table is in them. */
const droppedColumnsNote = (main: TableModel, banner: BannerPlan | null): string | null => {
  if (!banner) return null
  const shown = new Set(
    main.across.leaves.flatMap((leaf) => {
      const column = bannerColumnOf(leaf, banner)
      return column ? [column] : []
    })
  )
  const dropped = banner.columns.filter((column) => !shown.has(column))
  if (dropped.length === 0) return null
  const names = dropped.map((column) => `${column.groupDisplay}: ${column.display}`).join(", ")
  return `No respondents in: ${names}. ${
    dropped.length === 1 ? "This column is" : "These columns are"
  } not shown.`
}

/** A table built from its outputs, before its columns are renamed. */
interface Assembled {
  model: TableModel
  notes: string[]
  warnings: string[]
  /** Where the rows of each slot of the layout the table was built from are in `model`. */
  spans: SlotSpan[]
}

/**
 * The table of `plan` from its outputs: the rows of the layout the check
 * leaves, the statistics of the mean outputs and the letters, under the
 * engine's column names. `main`: the main output's model, when it is built.
 */
const assemble = (
  plan: TablePlan,
  contents: Contents,
  main: TableModel = buildTableModel(contents.main, plan.title)
): Assembled => {
  if (!hasRows(main)) throw new ResultError("The table came back empty.")
  // The rows are built from the layout the check leaves.
  const { layout, warnings } = afterCheck(plan, contents.check)
  const mean = readMeanGrid(contents.mean)
  const meanTotal = readMeanGrid(contents.meanTotal)
  checkFields(layout, mean, meanTotal)
  const modelOf = (content: RTableContent | undefined): TableModel | null =>
    plan.significance && content ? buildTableModel(content) : null
  const meanModel = modelOf(contents.mean)
  const excl0: MeanParts = {
    mean: readMeanGrid(contents.meanExcl0),
    meanTotal: readMeanGrid(contents.meanExcl0Total),
    meanModel: modelOf(contents.meanExcl0),
  }
  // A number table may test only the mean excluding 0: its significance is then that one's.
  const significance = tableSignificance(
    plan,
    main,
    meanModel?.significance ? meanModel : excl0.meanModel
  )
  const parts: Parts = {
    plan: layout === plan.layout ? plan : { ...plan, layout },
    main,
    keys: main.across.leaves.map(columnKey),
    mean,
    meanTotal,
    meanModel,
    excl0,
    significance,
  }

  const notes: string[] = []
  const { rows, links, spans } = layoutRows(parts, notes)
  const assembled = withLettersAt(withDownRows(main, rows), links)
  return {
    model: { ...assembled, title: plan.title, ...(significance ? { significance } : {}) },
    notes,
    warnings,
    spans,
  }
}

// ---------------------------------------------------------------------------
// A grid of the data whose rows are sibling containers, as one table
// ---------------------------------------------------------------------------

/** One sibling's table: built, or null when nobody in the base answered for it. */
interface SiblingBlock {
  part: SiblingPart
  /** The model of its main output. */
  main: TableModel
  table: Assembled | null
}

/** The roles of a sibling's outputs with their positions in the response, in script order. */
const partRoles = (part: SiblingPart): [OutputRole, number][] => {
  const { main, check, mean, meanTotal, meanExcl0, meanExcl0Total } = part.outputs
  const roles: [OutputRole, number | null][] = [
    ["main", main],
    ["check", check],
    ["mean", mean],
    ["mean-total", meanTotal],
    ["mean-excl-0", meanExcl0 ?? null],
    ["mean-excl-0-total", meanExcl0Total ?? null],
  ]
  return roles
    .filter((role): role is [OutputRole, number] => role[1] !== null)
    .sort((a, b) => a[1] - b[1])
}

/**
 * Nobody in the base answered for the sibling: the engine drops every row
 * with a count of 0 and returns the Totals block alone, at 0 (facts.md,
 * fact 7).
 */
const answeredByNobody = (main: TableModel): boolean => {
  const blocks = blocksOf(main)
  if (blocks.length === 0 || blocks.some((b) => !b.isTotal)) return false
  const counts = blocks
    .flatMap((b) => b.rows)
    .filter((row) => row.leaf.stat === STATISTIC_TYPE.N)
  return counts.length > 0 && counts.every((row) => row.cells.every((cell) => numeric(cell) === 0))
}

/**
 * The table of one sibling from its own outputs, built as the table of that
 * sibling alone. An output that is missing or cannot be read makes the whole
 * table an error that names the sibling: a gap is never left silently.
 */
const siblingBlock = (
  plan: TablePlan,
  part: SiblingPart,
  outputs: EngineOutputItem[]
): SiblingBlock => {
  const roles = partRoles(part)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { siblings: _siblings, ...table } = plan
  // The one table of the siblings is left as it is: no row sorted or hidden.
  const own: TablePlan = {
    ...table,
    outputs: roles.map(([role]) => role),
    layout: part.layout,
    check: part.check,
    sort: "none",
    hideEmpty: false,
  }
  try {
    const contents = readOutputs(own, roles.map(([, at]) => outputs[at]))
    const main = buildTableModel(contents.main, plan.title)
    return { part, main, table: answeredByNobody(main) ? null : assemble(own, contents, main) }
  } catch (e) {
    if (e instanceof ResultError) throw new ResultError(`${part.display}: ${e.message}`)
    throw e
  }
}

/** The columns of the one table, and where each built sibling has them. */
interface Columns {
  /** Every column any sibling's table has, in banner order, the Totals last. */
  leaves: AxisLeaf[]
  keys: string[]
  /** For each built sibling: its column of each column of the table, -1 where it has none. */
  from: number[][]
}

/**
 * The engine drops a column nobody in the table is in, and the Totals column
 * when one column is left (facts.md, fact 7), so the siblings' tables can
 * differ in their columns: the one table has every column any of them has.
 */
const unionColumns = (models: TableModel[], banner: BannerPlan | null): Columns => {
  const found = new Map<string, AxisLeaf>()
  models.forEach((m) =>
    m.across.leaves.forEach((leaf) => {
      const key = columnKey(leaf)
      if (!found.has(key)) found.set(key, leaf)
    })
  )
  const rank = (leaf: AxisLeaf): number => {
    if (leaf.isTotal) return Number.MAX_SAFE_INTEGER
    const column = banner ? bannerColumnOf(leaf, banner) : undefined
    return banner && column ? banner.columns.indexOf(column) : (banner?.columns.length ?? 0)
  }
  const sorted = [...found.entries()]
    .map(([key, leaf], i) => ({ key, leaf, i, rank: rank(leaf) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
  const keys = sorted.map((c) => c.key)
  return {
    leaves: sorted.map((c, at) => ({ ...c.leaf, elementIndex: at })),
    keys,
    from: models.map((m) => {
      const own = m.across.leaves.map(columnKey)
      return keys.map((key) => own.indexOf(key))
    }),
  }
}

/** The letter the engine gives the `k`th column it tests, from 0: A to Z, then A1 to Z1, A2 ... */
const sigColumnLetter = (k: number): string =>
  `${String.fromCharCode(65 + (k % 26))}${k >= 26 ? Math.floor(k / 26) : ""}`

const GROUPS_LEGEND = "Comparison Groups: "

/** The significance of the one table, and how each built sibling's letters read in it. */
interface MergedSignificance {
  meta: SigMeta
  /** For each built sibling: its element index to the table's (a column's index). */
  elements: Map<number, number>[]
  /** For each built sibling: its letter, in upper case, to the table's letter. */
  letters: Map<string, string>[]
  /** For each built sibling: the table's columns (not the Totals) where its base is low. */
  low: number[][]
}

/**
 * The significance of the one table: an element per column, lettered over
 * the table's columns as the engine letters the columns of one output (so a
 * sibling with every column keeps its letters). Each sibling's letters are
 * read through its own elements. A column is a low base where any sibling's
 * base in it is low, and carries the smallest sibling's base: a small
 * sibling's figures are never shown as tested (the larger siblings' cells
 * in that column are marked too, the safe side). `low` says whose bases are
 * low where, for the notes.
 */
const mergeSignificance = (
  models: TableModel[],
  columns: Columns
): MergedSignificance | null => {
  const metas = models.map((m) => m.significance)
  const first = metas.find((m): m is SigMeta => m !== undefined)
  if (!first) return null
  /** For each column: the element each sibling has there. */
  const found = columns.leaves.map((_, c) =>
    metas.flatMap((meta, b) => {
      const col = columns.from[b][c]
      if (!meta || col < 0) return []
      const index = meta.elements.findIndex((e) => e.span[0] <= col && col < e.span[1])
      if (index < 0) return []
      const element = meta.elements[index]
      const group = element.group === null ? undefined : meta.groups[element.group]
      return [{ b, index, element, group }]
    })
  )
  const groups: SigMeta["groups"] = []
  let k = 0
  const elements = columns.leaves.map((leaf, c): SigElement => {
    const own = found[c]
    const letter = leaf.isTotal ? null : sigColumnLetter(k++)
    const label = own.find((f) => f.group !== undefined)?.group?.label
    let group: number | null = null
    if (letter && label !== undefined) {
      group = groups.findIndex((g) => g.label === label)
      if (group < 0) group = groups.push({ label, letters: "", overlap: false }) - 1
      groups[group].letters += letter
      groups[group].overlap = groups[group].overlap || own.some((f) => f.group?.overlap === true)
    }
    const bases = own.map((f) => f.element.baseN)
    return {
      span: [c, c + 1],
      header: [...leaf.path],
      letter,
      group,
      baseN: bases.length > 0 ? Math.min(...bases) : 0,
      lowBase: own.some((f) => f.element.lowBase),
      total: leaf.isTotal,
    }
  })
  const low = metas.map((_, b) =>
    columns.leaves.flatMap((leaf, c) =>
      !leaf.isTotal && found[c].some((f) => f.b === b && f.element.lowBase) ? [c] : []
    )
  )
  const elementMaps = metas.map((_, b) => {
    const map = new Map<number, number>()
    found.forEach((list, c) => list.forEach((f) => f.b === b && map.set(f.index, c)))
    return map
  })
  const letterMaps = metas.map((meta, b) => {
    const map = new Map<string, string>()
    elementMaps[b].forEach((c, index) => {
      const own = meta?.elements[index]?.letter
      const letter = elements[c].letter
      if (own && letter) map.set(own.toUpperCase(), letter)
    })
    return map
  })
  const legend = first.legend.map((line) =>
    line.startsWith(GROUPS_LEGEND)
      ? `${GROUPS_LEGEND}${groups.map((g) => g.letters).join("/")}`
      : line
  )
  const notes = [...new Set(metas.flatMap((m) => m?.notes ?? []))]
  return {
    meta: { ...first, bannerAxis: "across", elements, groups, legend, notes },
    elements: elementMaps,
    letters: letterMaps,
    low,
  }
}

/** "a", "a and b", "a, b and c". */
const andList = (items: string[]): string =>
  items.length === 1 ? items[0] : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`

/**
 * Columns `cols` of the one table in words, as the table shows them, each
 * group's heading once: "Gender: Male, Female and Other", groups apart with
 * "; ".
 */
const columnWords = (cols: number[], columns: Columns, banner: BannerPlan | null): string => {
  const groups: { heading: string | null; names: string[] }[] = []
  cols.forEach((c) => {
    const leaf = columns.leaves[c]
    const column = banner ? bannerColumnOf(leaf, banner) : undefined
    const heading = column
      ? column.groupDisplay
      : leaf.path.length > 1
        ? leaf.path.slice(0, -1).join(": ")
        : null
    const name = column ? column.display : leaf.path[leaf.path.length - 1]
    const last = groups[groups.length - 1]
    if (last && last.heading === heading) last.names.push(name)
    else groups.push({ heading, names: [name] })
  })
  return groups.map((g) => `${g.heading ? `${g.heading}: ` : ""}${andList(g.names)}`).join("; ")
}

/** The letters of a `sig` cell as the one table letters its columns; a letter it does not have is dropped. */
const translateText = (text: string, letters: Map<string, string>): string =>
  (text.match(LETTER) ?? [])
    .flatMap((token) => {
      const letter = letters.get(token.toUpperCase())
      if (!letter) return []
      return [token === token.toUpperCase() ? letter : letter.toLowerCase()]
    })
    .join("")

/**
 * A cell of sibling `b`'s table in the one table: its letters as the table
 * letters its columns, and the cell that prints them where it now is
 * (`moveTo`; dropped when that cell is not in the table).
 */
const translateCell = (
  cell: ModelCell,
  b: number,
  sig: MergedSignificance | null,
  moveTo: (at: { row: number; col: number }) => { row: number; col: number } | undefined
): ModelCell => {
  let next = cell
  if (cell.sig) {
    const letters = cell.sig.letters
      .flatMap((l): CellSigLetter[] => {
        const c = sig?.elements[b].get(l.element)
        const letter = c === undefined ? null : sig?.meta.elements[c].letter
        if (c === undefined || !letter) return []
        return [
          { ...l, element: c, letter: l.level === "lower" ? letter.toLowerCase() : letter },
        ]
      })
      .sort((x, y) => x.element - y.element)
    const moved: CellSig = { ...cell.sig, letters }
    delete moved.lettersAt
    const at = cell.sig.lettersAt ? moveTo(cell.sig.lettersAt) : undefined
    if (at) moved.lettersAt = at
    next = { ...cell, sig: moved }
  }
  if (sig && cell.role === "letters" && typeof cell.raw === "string")
    next = { ...next, raw: translateText(cell.raw, sig.letters[b]) }
  return next
}

/**
 * The cell of a column that a sibling's table does not have: nobody of that
 * sibling is in the column, so a count is 0 and anything else is empty. The
 * Totals column, which the engine drops when one column is left, is not
 * known.
 */
const missingCell = (row: ModelCell[], stat: string | undefined, total: boolean): ModelCell => {
  const template = row[0]
  const letters = template?.role === "letters"
  return {
    raw: letters ? "" : !total && COUNT_STATS.includes(stat ?? "") ? 0 : null,
    stat: template?.stat ?? stat ?? "",
    kind: template?.kind ?? "count",
    format: template?.format ?? null,
    ...(letters ? { role: "letters" as const } : {}),
  }
}

/** The cells of a row that names a sibling: empty. */
const HEADING_CELL: ModelCell = { raw: null, stat: "", kind: "text", format: null }

/**
 * The rows of a finished table that name a sibling (a grid of the data from
 * sibling containers, stacked): the only rows without a statistic.
 */
export const siblingHeadingRows = (model: TableModel): number[] =>
  model.statAxis !== "down"
    ? []
    : model.down.leaves.flatMap((leaf, r) => (leaf.stat === undefined && !leaf.isTotal ? [r] : []))

/**
 * A sibling's base in its Totals column, or in its one column when the
 * engine dropped the Totals column ("Bank 1 412", "Bank 1 412 (weighted
 * 398)"); "not known" when the engine returned no base (one row with answers).
 */
const baseWords = (block: SiblingBlock): string => {
  const { main, part } = block
  const total = main.across.leaves.findIndex((leaf) => leaf.isTotal)
  const col = total >= 0 ? total : main.across.leaves.length === 1 ? 0 : -1
  const count = (stat: string): number | null => {
    const r = main.down.leaves.findIndex((leaf) => leaf.isTotal && leaf.stat === stat)
    return r < 0 || col < 0 ? null : numeric(main.cells[r]?.[col])
  }
  const n = count(STATISTIC_TYPE.N)
  if (n === null) return `${part.display} not known`
  const weighted = count(STATISTIC_TYPE.BIG_N)
  return `${part.display} ${n}${weighted === null ? "" : ` (weighted ${Math.round(weighted)})`}`
}

/** A row of the one table: a row of built sibling `b`'s table (row `r`), or a row that names a sibling. */
type Placed = { leaf: AxisLeaf; b: number; r: number } | { leaf: AxisLeaf; b: null }

/**
 * A grid of the data whose rows are sibling containers, as one table, from
 * the outputs of every sibling's table. "stacked": a block per sibling, in
 * instance order: a row that names it, its rows as its own table shows them,
 * then its Base. "row": one row per sibling, the chosen row of its table,
 * under the sibling's name; the bases are a note under the table. The
 * letters of each row are its sibling's. A sibling nobody in the base
 * answered has no rows and a note that says so.
 */
const siblingResult = (
  plan: TablePlan,
  siblings: SiblingPlan,
  outputs: EngineOutputItem[]
): TableResult => {
  const blocks = siblings.parts.map((part) => siblingBlock(plan, part, outputs))
  const built = blocks.filter(
    (block): block is SiblingBlock & { table: Assembled } => block.table !== null
  )
  if (built.length === 0)
    throw new ResultError("Nobody in the base has an answer, so the table has no rows.")
  const models = built.map((block) => block.table.model)
  const columns = unionColumns(models, plan.banner)
  const sig = mergeSignificance(models, columns)
  const row = siblings.layout === "row" ? siblings.row : null
  /** The chosen row as the table shows it. */
  const rowShown =
    row === null
      ? null
      : (siblings.parts
          .map((part) => slotNamed(part.layout, row))
          .flatMap((slot) => (slot && slot.kind !== "base" ? [slot.display] : []))[0] ?? row)

  const notes: string[] = []
  const placed: Placed[] = []
  let element = 0
  blocks.forEach((block) => {
    const { display } = block.part
    if (row === null)
      placed.push({
        leaf: { id: display, path: [display], isTotal: false, elementIndex: element++ },
        b: null,
      })
    if (!block.table) {
      notes.push(
        row === null
          ? `${display} has no answers in this table's base, so its block has no rows.`
          : `${display} has no answers in this table's base, so it has no row.`
      )
      return
    }
    const b = built.indexOf(block as SiblingBlock & { table: Assembled })
    const { spans, model } = block.table
    const place = (span: SlotSpan, leafOf: (leaf: AxisLeaf) => AxisLeaf): void => {
      for (let r = span.from; r < span.to; r++)
        placed.push({ leaf: { ...leafOf(model.down.leaves[r]), elementIndex: element }, b, r })
      element++
    }
    if (row === null) {
      // The Base comes last in a block: the rows a sibling's table shows, then its base.
      const ordered = [
        ...spans.filter((s) => s.slot.kind !== "base"),
        ...spans.filter((s) => s.slot.kind === "base"),
      ]
      ordered.forEach((span) => place(span, (leaf) => ({ ...leaf, id: `${display}//${leaf.id}` })))
      return
    }
    const slot = slotNamed(
      spans.map((s) => s.slot),
      row
    )
    const span = spans.find((s) => s.slot === slot)
    if (!span) {
      notes.push(`${display} is not shown: it has no row ${rowShown}.`)
      return
    }
    place(span, (leaf) => ({ ...leaf, id: `${display}#${leaf.stat ?? ""}`, path: [display] }))
  })

  const where = new Map<string, number>()
  placed.forEach((p, at) => {
    if (p.b !== null) where.set(`${p.b}:${p.r}`, at)
  })
  const cells = placed.map((p): ModelCell[] => {
    if (p.b === null) return columns.keys.map(() => ({ ...HEADING_CELL }))
    const { b, r } = p
    const own = built[b].table.model.cells[r] ?? []
    const moveTo = (at: { row: number; col: number }) => {
      const to = where.get(`${b}:${at.row}`)
      const col = columns.from[b].indexOf(at.col)
      return to === undefined || col < 0 ? undefined : { row: to, col }
    }
    return columns.from[b].map((c, col) =>
      c < 0
        ? missingCell(own, p.leaf.stat, columns.leaves[col].isTotal)
        : translateCell(own[c], b, sig, moveTo)
    )
  })

  const deepest = models.reduce((a, m) => (m.freeze.y > a.freeze.y ? m : a))
  const leaves = placed.map((p) => p.leaf)
  const model: TableModel = {
    title: plan.title,
    kind: deepest.kind,
    statAxis: deepest.statAxis,
    across: {
      depth: Math.max(0, ...columns.leaves.map((leaf) => leaf.path.length)),
      leaves: columns.leaves,
      tree: buildHeaderTree(columns.leaves),
    },
    down: {
      depth: Math.max(0, ...leaves.map((leaf) => leaf.path.length)),
      leaves,
      tree: buildHeaderTree(leaves),
    },
    cells,
    freeze: deepest.freeze,
    ...(sig ? { significance: sig.meta } : {}),
  }

  // A column a sibling's table does not have: nobody of that sibling is in it.
  const missing = built.flatMap((block, b) => {
    const absent = columns.leaves.flatMap((leaf, c) =>
      !leaf.isTotal && columns.from[b][c] < 0 ? [c] : []
    )
    return absent.length > 0
      ? [`${block.part.display}: no respondents in ${columnWords(absent, columns, plan.banner)}.`]
      : []
  })
  // A sibling's low bases: the column is marked for every sibling, the note says whose.
  const lowBases = built.flatMap((block, b) => {
    const cols = sig?.low[b] ?? []
    if (!sig || cols.length === 0) return []
    const where = columnWords(cols, columns, plan.banner)
    return [`${block.part.display}: base below ${sig.meta.minBase} in ${where}.`]
  })
  const dropped = droppedColumnsNote(model, plan.banner)
  return {
    model: renameColumns(model, plan.banner),
    notes: [
      ...(row === null ? [] : [`Bases: ${blocks.map(baseWords).join(", ")}`]),
      ...siblingSentences(
        built.map((block) => ({ display: block.part.display, list: block.table.notes }))
      ),
      ...notes,
      ...missing,
      ...lowBases,
      ...(dropped ? [dropped] : []),
    ],
    warnings: siblingSentences(
      built.map((block) => ({ display: block.part.display, list: block.table.warnings }))
    ),
  }
}

export const buildResult = (plan: TablePlan, outputs: EngineOutputItem[]): TableResult => {
  if (plan.siblings) return siblingResult(plan, plan.siblings, outputs)
  const contents = readOutputs(plan, outputs)
  const main = buildTableModel(contents.main, plan.title)
  const { model, notes, warnings } = assemble(plan, contents, main)
  const dropped = droppedColumnsNote(main, plan.banner)
  return {
    // Sorted and without its empty rows as the plan asks, before it is kept or exported.
    model: arrangeRows(renameColumns(model, plan.banner), plan),
    notes: dropped ? [...notes, dropped] : notes,
    warnings,
  }
}
